import { exchange, SignalError, signOff } from "../../lib/webrtc";

/**
 * Publishing a MediaStream over WHIP.
 *
 * WHIP is WebRTC-HTTP Ingestion: one POST of an SDP offer, one SDP answer back,
 * and the browser is publishing. Everything that makes streaming hard -
 * hardware encoding, congestion control, packet loss recovery, reconnection
 * after a network change - is the WebRTC stack's problem rather than ours.
 *
 * The alternative in a browser is `MediaRecorder` over a WebSocket, which means
 * owning the buffering, the backpressure and the reconnect logic, and getting
 * several seconds of latency for the trouble.
 *
 * ## The URL is a secret
 *
 * It comes from `GET /me/station/stream`, which is authenticated and behind the
 * studio flag, and anybody holding it can broadcast to the station. It is not
 * stored, not logged, and not put anywhere a listener can reach - see
 * `StreamIngest` on the backend, which is the only thing that ever hands it out.
 */

export interface Publication {
  /** `connected`, `disconnected`, `failed`, `closed`. */
  onStateChange(handler: (state: RTCPeerConnectionState) => void): void;
  /**
   * Swap what is being sent as video, without renegotiating.
   *
   * `replaceTrack` keeps the sender, its codec preferences and the SDP as they
   * are - so switching between the composited canvas and a camera mid-broadcast
   * costs a frame, not a reconnection.
   */
  replaceVideo(track: MediaStreamTrack): Promise<void>;
  stop(): Promise<void>;
}

/**
 * Publish a stream, resolving once the ingest endpoint has accepted the offer.
 *
 * Resolving on the answer rather than on `connected` is deliberate: the answer
 * is when the *server* has agreed, and ICE can legitimately take several more
 * seconds. A host whose button spins through ICE negotiation assumes it has
 * hung. The connection state is reported afterwards instead.
 */
export async function publish(
  url: string,
  stream: MediaStream,
): Promise<Publication> {
  const pc = new RTCPeerConnection({ iceServers: [] });

  let videoSender: RTCRtpSender | null = null;

  for (const track of stream.getTracks()) {
    const transceiver = pc.addTransceiver(track, {
      direction: "sendonly",
      streams: [stream],
    });

    if (track.kind === "video") {
      preferH264(transceiver);
      videoSender = transceiver.sender;
    }
  }

  let accepted;

  try {
    accepted = await exchange(url, pc);
  } catch (cause) {
    pc.close();

    if (cause instanceof SignalError && cause.status === 404) {
      /*
       * The one worth saying apart. A 404 here is the live input not existing
       * any more - rotated from another tab, or deleted in the Cloudflare
       * dashboard - and reloading re-fetches credentials, which fixes it. Every
       * other status is ours to look at rather than the host's.
       */
      throw new SignalError(
        "This broadcast link is no longer valid. Reload the page to get a new one.",
        404,
        { cause },
      );
    }

    throw cause;
  }

  await pc.setRemoteDescription({ type: "answer", sdp: accepted.answer });

  const handlers: ((state: RTCPeerConnectionState) => void)[] = [];

  pc.addEventListener("connectionstatechange", () => {
    for (const handler of handlers) handler(pc.connectionState);
  });

  return {
    onStateChange: (handler) => {
      handlers.push(handler);
      handler(pc.connectionState);
    },
    replaceVideo: async (track) => {
      await videoSender?.replaceTrack(track);
    },
    stop: async () => {
      pc.close();
      await signOff(accepted.resource);
    },
  };
}

/**
 * Ask for H.264 ahead of anything else.
 *
 * ## This is not an optimisation - without it there is no broadcast
 *
 * Chromium offers VP8 first and will happily agree on it with a peer that
 * accepts it. Cloudflare Stream does not: its WHIP endpoint takes H.264 video
 * and Opus audio, and an offer without H.264 is refused outright.
 *
 * This used to be worse and quieter. The old self-hosted ingest accepted VP8,
 * connected, reported `connected`, cut segments - and published audio only,
 * because its HLS muxer silently dropped the video track. Every signal a
 * broadcaster would look at said the stream was fine. A refusal at the
 * handshake is the better failure of the two by a wide margin.
 *
 * Preferred rather than forced: the remaining codecs stay in the list behind
 * H.264, so a browser without it still produces a valid offer and gets a clear
 * rejection rather than failing somewhere further down with a worse message.
 */
function preferH264(transceiver: RTCRtpTransceiver): void {
  const codecs = RTCRtpSender.getCapabilities("video")?.codecs;

  if (!codecs || typeof transceiver.setCodecPreferences !== "function") return;

  const h264 = codecs.filter((c) => c.mimeType.toLowerCase() === "video/h264");

  if (h264.length === 0) return;

  transceiver.setCodecPreferences([
    ...h264,
    ...codecs.filter((c) => c.mimeType.toLowerCase() !== "video/h264"),
  ]);
}
